/**
 * Premium Countdown Timer Custom Element
 * Handles date parsing, ticking logic, expired state transitions, and memory cleanup.
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.endDate = this.parseEndDate(this.dataset.endDate);
    this.unitEls = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
    this.unitsWrap = this.querySelector('.countdown-deal__units');
    this.expiredEl = this.querySelector('.countdown-deal__expired');

    if (!this.endDate) {
      // No valid date configured yet (merchant hasn't set one, or it's malformed) — fail safe:
      // don't run a timer against an invalid date, just show the expired/fallback state so nothing displays "NaN" or a frozen 00:00:00.
      this.showExpired();
      return;
    }

    this.tick(); // run once immediately so there's no 1-second flash of stale "00" values on load
    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  parseEndDate(raw) {
    if (!raw) return null;
    // Merchant enters "YYYY-MM-DD HH:MM" (per the schema's info text). That format is NOT reliably parsed
    // by `new Date()` across all browsers (notably Safari). Convert it to an ISO-8601 string first:
    //   "2026-08-15 23:59"  ->  "2026-08-15T23:59:00"
    const trimmed = raw.trim();
    const isoString = trimmed.replace(' ', 'T') + (trimmed.length <= 16 ? ':00' : '');
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  }

  tick() {
    const diff = this.endDate.getTime() - Date.now();
    if (diff <= 0) {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      this.showExpired();
      return;
    }
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    this.unitEls.days.textContent = String(days).padStart(2, '0');
    this.unitEls.hours.textContent = String(hours).padStart(2, '0');
    this.unitEls.minutes.textContent = String(minutes).padStart(2, '0');
    this.unitEls.seconds.textContent = String(seconds).padStart(2, '0');
  }

  showExpired() {
    if (this.unitsWrap) this.unitsWrap.hidden = true;
    if (this.expiredEl) this.expiredEl.hidden = false;
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
